import * as Sentry from '@sentry/node';
import axios, { AxiosResponse } from "axios";
import { AsyncReturnType, Unpromise } from "../types";
import {
  getLocalStorage,
  getSessionStorage,
  removeLocalStorage,
  setLocalStorage,
  setSessionStorage
} from "../utils/local-storage";


// declare module 'axios' {
//   export interface AxiosResponse<T = any> extends Promise<T> {}
// }

const BUILD_ID = process.env.REACT_APP_BUILD_ID;

export const API_BASE_URI = process.env.REACT_APP_API_BASE_URI || "/api";
export const HEADER_BUILD_ID = "X-Reclaim-Build-Id";

export function factory(path?: string) {
  const baseURL = path ? [API_BASE_URI, path].join("/") : API_BASE_URI;
  const client = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      [HEADER_BUILD_ID]: BUILD_ID || "unknown",
      post: {
        "Content-Type": "application/json; charset=utf-8",
      },
    },
  });

  // FIXME: (IW) Jenky masquerade implementation, but it works for now.
  client.interceptors.request.use((config) => {
    const impersonate = getSessionStorage("auth.impersonate");
    const admin = getLocalStorage("auth.admin");

    if (impersonate) {
      const failed = getSessionStorage("auth.impersonate.error");
      const user = getLocalStorage("auth.user");

      if (!failed && ((!!admin && admin.admin) || (!admin && !!user && user.admin && !user.impersonated))) {
        if (!admin) setLocalStorage("auth.admin", user);
        config.headers["X-Reclaim-Impersonate"] = impersonate;
      }
    } else if (admin) {
      setLocalStorage("auth.user", admin);
      removeLocalStorage("auth.admin");
    }

    return config;
  });

  client.interceptors.response.use(undefined, (error) => {
    if (/^40[01]$/.test(error?.response?.status) && !!getSessionStorage("auth.impersonate")) {
      const admin = getLocalStorage("auth.admin");
      if (admin) setLocalStorage("auth.user", admin);
      setSessionStorage("auth.impersonate.error", {
        message: error.message,
        response: error.response,
      });

      console.warn(`Impersonation disabled because authentication failed.
      Make sure "auth.impersonate" is set correctly, then clear
      "auth.impersonate.error" (in sessionStorage) to resume impersonation.`);
    }
    if (/^5\d+$/.test(error?.response?.status)) {
      const user = getLocalStorage("auth.user");

      let errorScope = new Sentry.Scope();
      errorScope = errorScope.setLevel(Sentry.Severity.Error)
      errorScope = errorScope.setUser(user)
      errorScope = errorScope.setContext('request_config', error.config)

      Sentry.captureMessage(`[SERVER ERROR] ${error?.message}: ${JSON.stringify(error?.response)}`, errorScope);
    }

    return Promise.reject(error?.response || error);
  });

  return client;
}

export function cancelablePromise<T extends (...args: any[]) => Promise<AxiosResponse<any>>>(
  apiCall: T
): (...funcArgs: Parameters<T>) => Promise<AsyncReturnType<T>> {
  return (...args: Parameters<T>): Promise<AsyncReturnType<any>> => {
    const source = axios.CancelToken.source();

    const axiosPromise = apiCall(...(args as any));

    Object.assign(axiosPromise, {
      cancel: () => {
        source.cancel(`Query was cancelled by React Query`);
      },
      name: apiCall.name,
    });

    return axiosPromise;
  };
}

export function justData<APICall extends (...args: any) => Promise<AxiosResponse<any>>>(
  apiCall: APICall
): (...funcArgs: Parameters<APICall>) => Promise<Unpromise<ReturnType<APICall>>["data"]> {
  return async function (...args: any[]) {
    const { data } = await apiCall(...args);
    return data;
  } as any;
}

export default factory();
